import { StatsigClient } from '@statsig/react-bindings'

import { STATSIG_EXPERIMENTS, STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { ENABLE_SVD_FLOW_DISCOVERY, SVD_APP_WEBVIEW_GATE } from '@src/config/svd-flags'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'

/**
 * Non-hook read of the `ramp_svd-rollout-web` experiment for service-layer
 * code (cart/checkout enrichment, product cards, collection) that runs outside
 * React. Replaces the removed `smart_variant_enabled` gate — the on/off
 * decision now comes from the discovery team's SVD rollout experiment, with
 * semantics identical to `useIsSvdEnabled`:
 * - dark while the program-wide `ENABLE_SVD_FLOW_DISCOVERY` kill-switch is off
 *   (no experiment read, no Statsig exposure),
 * - on only when the user resolves to the `show` group.
 *
 * `StatsigClient.instance()` resolves the client registered by the
 * StatsigProvider. On the server, or when no client exists yet, every failure
 * mode degrades to `false` so callers fall back to the v1 paths.
 *
 * Native-app web-views read their own gate (`is_svd_enabled_on_app`) instead —
 * mirroring the server-side split in `getServerSideBaseProps` — so app traffic
 * never logs a web-experiment exposure and both sources agree per request.
 */
export const getSvdExperimentValue = (): boolean => {
  if (typeof window === 'undefined') return false
  if (!ENABLE_SVD_FLOW_DISCOVERY) return false
  try {
    if (getIsNativeViewClient()) {
      return StatsigClient.instance().checkGate(SVD_APP_WEBVIEW_GATE)
    }
    const experiment = StatsigClient.instance().getExperiment(STATSIG_EXPERIMENTS.RAMP_SVD_ROLLOUT)
    return experiment.groupName === STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.RAMP_SVD_ROLLOUT].buckets?.show
  } catch (error) {
    console.error('Failed while checking SVD rollout experiment outside React', error)
    return false
  }
}
